// Client mirror of the server driver registry shape (services/fpaDrivers.ts),
// plus the unit helpers the guided inputs use to render/parse values. The
// registry itself is fetched from /fpa/driver-registry (single source of truth);
// these helpers just translate between stored values and what the operator sees.

export type DriverUnit = 'ratio' | 'currency' | 'rate' | 'hours' | 'months' | 'count';
export type DriverScope = 'department' | 'company';

export interface DriverDef {
  key: string;
  label: string;
  description: string;
  unit: DriverUnit;
  scope: DriverScope;
  group: string;
  default_value: number | null;
  /** Planning v2 — present on the DB-backed registry response. */
  is_builtin?: boolean;
  is_active?: boolean;
}

/** Short suffix shown next to an input (e.g. the "%" or "/hr"). */
export function unitSuffix(unit: DriverUnit): string {
  switch (unit) {
    case 'ratio': return '%';
    case 'rate': return '$/hr';
    case 'currency': return '$';
    case 'hours': return 'hrs/mo';
    case 'months': return 'mo';
    case 'count': return '';
  }
}

export function unitPlaceholder(unit: DriverUnit): string {
  switch (unit) {
    case 'ratio': return 'e.g. 80';
    case 'rate': return 'e.g. 185';
    case 'currency': return 'e.g. 50000';
    case 'hours': return 'e.g. 160';
    case 'months': return 'e.g. 1';
    case 'count': return 'e.g. 5';
  }
}

/** Stored numeric → the string shown in the input. Ratios are stored 0–1 but
 *  edited as a percentage. */
export function toInputValue(unit: DriverUnit, stored: number | null): string {
  if (stored == null) return '';
  return unit === 'ratio' ? String(+(stored * 100).toFixed(4)) : String(stored);
}

/** Input string → stored numeric (null clears). Ratios divide by 100. */
export function fromInputValue(unit: DriverUnit, input: string): number | null {
  const t = input.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return unit === 'ratio' ? n / 100 : n;
}

/** Stored numeric → a compact display string for tables. */
export function formatDriverValue(unit: DriverUnit, stored: number | null): string {
  if (stored == null) return '—';
  switch (unit) {
    case 'ratio': return `${+(stored * 100).toFixed(2)}%`;
    case 'rate': return `$${stored}/hr`;
    case 'currency': return `$${stored.toLocaleString()}`;
    case 'hours': return `${stored} hrs/mo`;
    case 'months': return `${stored} mo`;
    case 'count': return String(stored);
  }
}

/** Human label for an assumption key: registry label, fixed-cost line, or raw. */
export function assumptionKeyLabel(key: string, registry: DriverDef[]): string {
  const d = registry.find((r) => r.key === key);
  if (d) return d.label;
  if (/^cost\.fixed\./.test(key)) return `Fixed: ${key.replace(/^cost\.fixed\./, '')}`;
  return key;
}

/** Unit for a key (fixed-cost lines are currency); defaults to currency. */
export function unitForKey(key: string, registry: DriverDef[]): DriverUnit {
  const d = registry.find((r) => r.key === key);
  if (d) return d.unit;
  return 'currency';
}
